/**
 * THE BOOTH CAST — three people, not four narrators.
 *
 * D-006 decided the shape of this a long time before the code existed:
 * *"Booth pair plus a pit/sideline reporter as recurring characters."*
 * `narrate.ts` has always chosen a ROLE per beat (`pbp` / `colour` / `pit` /
 * `feature`), and the director has always decided who *would* speak. What was
 * missing is that nobody was a PERSON. Four role strings produce four registers;
 * they do not produce a broadcast, because a broadcast is people talking to each
 * other.
 *
 * THE DISTINCTION THIS MODULE EXISTS TO DRAW:
 *
 *   • a ROLE is what KIND of line this is — a call, an argument, a report.
 *   • a MEMBER is WHO says it.
 *
 * They are not the same thing and mapping them 1:1 was the mistake. There are
 * four kinds of line and three people, because the sign-off at the end of a race
 * is a different kind of line from the lap-by-lap call but it comes out of the
 * same host's mouth. Collapsing them would have forced a fourth chair into the
 * booth that nobody asked for and that would speak twice a race.
 *
 * WHY THE CHARACTER BRIEFS ARE **NOT** IN THIS FILE.
 *
 * The prose brief that shapes each voice lives in `worker/index.ts`, keyed by
 * member id, and this module sends only the id. That is deliberate and it is a
 * security property rather than a tidiness one: the request is composed in the
 * browser, and anything the browser can put in the body is something an attacker
 * can put in the body. A `voice` string travelling over the wire and landing in
 * a system prompt is prompt injection with the door held open. **The client
 * names the speaker; the server decides what that speaker sounds like.**
 *
 * Everything here is deterministic. No part of the cast needs the model to be
 * reachable — with narration off, the names, the seats and the handoffs still
 * work, because they are all functions of the beat.
 */

import type { BoothBeat } from './booth.ts';
import { hash as stableHash } from './kit.ts';

/** What kind of line this is. Unchanged — `narrate.ts` has always produced these. */
export type NarrateRole = 'pbp' | 'colour' | 'pit' | 'feature';

/** Who says it. Three chairs. */
export type MemberId = 'host' | 'analyst' | 'pit';

export interface BoothMember {
  id: MemberId;
  name: string;
  /** What the other two call him or her, and what a handoff ends on. */
  short: string;
  /** Printed under the name in the interface. */
  seat: string;
  /** One line of who they are, for the interface and for nobody else. */
  bio: string;
  /** Two letters for the badge. */
  initials: string;
  /** D-EZ-111 — the pool entry this chair was filled from (stable across saves). */
  poolId?: string;
  /** D-EZ-111 — how the others refer to this person. */
  pronoun?: 'he' | 'she';
}

/**
 * The three of them.
 *
 * Fictional, like every other name in this league — the generator has been
 * producing Otis Barkleys and Chase Coffeys since D-024 and the booth should
 * sound like it comes from the same world. No real broadcaster is named here and
 * none should be.
 *
 * ⚠ RECAST 17 Aug 2026. Howard: *"I guess one of the commentators becomes the
 * girl and the sideline becomes the boy."* The booth pair is now a woman and a
 * man and the reporter on track is a man.
 *
 * Done by SWAPPING THE TWO EXISTING NAMES between chairs rather than inventing
 * anybody: Reba Vance moves up into the analyst's seat and Ned Coyle goes down
 * to pit road. The BRIEFS travelled with the CHAIR, not with the person — the
 * analyst is the one who drove the tour, the reporter is the one standing at the
 * car — because those briefs are what the chair is FOR. A booth with two people
 * calling position and nobody explaining the car is not a recast, it is a
 * broadcast with a hole in it.
 */
export const CAST: Record<MemberId, BoothMember> = {
  host: {
    id: 'host',
    name: 'Marty Kell',
    short: 'Marty',
    seat: 'Play-by-play',
    bio: 'Twenty-two seasons in the chair. Calls it straight and gets out of the way.',
    initials: 'MK',
    poolId: 'marty-kell',
    pronoun: 'he',
  },
  analyst: {
    id: 'analyst',
    name: 'Reba Vance',
    short: 'Reba',
    seat: 'Analyst',
    bio: 'Eleven years on the tour, two wins at the short tracks. Knows what the car is telling the driver.',
    initials: 'RV',
    poolId: 'reba-vance',
    pronoun: 'she',
  },
  pit: {
    id: 'pit',
    name: 'Ned Coyle',
    short: 'Ned',
    seat: 'On track',
    bio: 'Pit road and the garage. First to the car, every time.',
    initials: 'NC',
    poolId: 'ned-coyle',
    pronoun: 'he',
  },
};

export const CAST_LIST: readonly BoothMember[] = [CAST.host, CAST.analyst, CAST.pit];

/** Every name the cast may say out loud, so the verifier does not refuse them. */
export const CAST_WORDS: readonly string[] = CAST_LIST.flatMap((m) => [
  ...m.name.split(/\s+/),
  m.short,
]);

/**
 * Four kinds of line, three people.
 *
 * `feature` — the reflective close of a race — goes to the HOST rather than to a
 * fourth chair. A sign-off is the host's job on every broadcast ever made, and
 * the alternative was inventing a "feature writer" who speaks once a race and is
 * a stranger the rest of the time.
 */
export function memberFor(role: NarrateRole): BoothMember {
  if (role === 'colour') return CAST.analyst;
  if (role === 'pit') return CAST.pit;
  // 'pbp' and 'feature' are both the host — the call and the sign-off.
  return CAST.host;
}

// ---------------------------------------------------------------------------
// Handoffs
// ---------------------------------------------------------------------------

/**
 * The host throwing to somebody, and them answering. This is the whole
 * difference between three narrators and a broadcast.
 *
 * IT IS RATIONED, AND THAT IS THE POINT. §8.1 of NARRATION_MODEL is that the
 * booth's first job is choosing silence, and a handoff is the most repetitive
 * thing a broadcast does — the same six words, from the same person, to the same
 * person. A booth that throws on every change of voice is a booth that says
 * "Ned, what are you seeing?" fourteen times an afternoon, which is worse than
 * never throwing at all.
 *
 * Three bars, all of them deterministic:
 *
 *   1. the speaker actually CHANGED, and the incoming voice is not the host —
 *      you do not throw to the man whose show it is;
 *   2. the beat has enough behind it to be worth interrupting for;
 *   3. at least `HANDOFF_GAP` beats since the last one.
 *
 * Measured rather than assumed — `npm run booth` counts them per race.
 */
export const HANDOFF_GAP = 3;
export const HANDOFF_MIN_FACTS = 2;

export interface Handoff {
  from: MemberId;
  to: MemberId;
  /** The throw itself, e.g. "Ned?" — written by the model, or the fallback below. */
  cue: string;
}

/**
 * Would this beat carry a handoff? Pure, so the rate can be counted without a
 * network and without a race.
 *
 * `previousSpeaker` is null on the first beat of a race — nothing to hand off
 * FROM, and a broadcast that opens on a throw has skipped its own opening.
 */
export function handoffDue(
  beat: BoothBeat,
  incoming: MemberId,
  previousSpeaker: MemberId | null,
  beatsSinceLastHandoff: number,
): boolean {
  if (previousSpeaker === null) return false;
  if (previousSpeaker === incoming) return false;
  if (incoming === 'host') return false;
  if (beat.facts.length < HANDOFF_MIN_FACTS) return false;
  if (beatsSinceLastHandoff < HANDOFF_GAP) return false;
  return true;
}

/**
 * What the throw says when the model is off, unreachable or refused.
 *
 * ⚠ THIS WAS ONE CUE PER ANGLE AND THE HARNESS CAUGHT IT ON ITS FIRST RUN.
 *
 * Six angles, one line each, and the reasoning was that the angle is already a
 * fact about the moment so the table would rotate by itself as the race changed
 * shape. It does not. Measured over sixty real races, handoffs land on exactly
 * THREE angles — `consequence`, `season` and `stretch` — because those are the
 * angles that send the beat to somebody who is not the host, which is the very
 * condition a handoff requires. So 155 throws drew on three sentences, and
 * "Reba, what have you got down there?" arrived 54 times in 60 races.
 *
 * The rotation was fictional in exactly the way §8.2 warns about: variety that
 * is a property of a table rather than a property of the game state is not
 * variety, and the tests could not see it because a synthetic race visits every
 * angle evenly and a real one does not.
 *
 * Fixed by giving each angle a POOL and indexing it on the turn number. Still
 * completely deterministic — the same beat always produces the same throw, which
 * is what keeps a replayed race identical — but it now varies with the race
 * rather than with a classification of it. Handoffs are at least `HANDOFF_GAP`
 * beats apart, so consecutive throws land on different turns and therefore on
 * different entries.
 */
const CUES: Record<string, readonly string[]> = {
  consequence: [
    '{n}, what have you got down there?',
    '{n}, how bad does it look?',
    "{n}, you're right on top of it.",
    '{n}, tell us what you can see.',
    '{n}, what is it like where you are?',
    '{n}, give me the damage.',
    '{n}, who is it going to cost?',
    '{n}, is anybody moving down there?',
  ],
  season: [
    '{n}, put that in context for me.',
    '{n}, how big is that for him?',
    '{n}, we have been waiting on this.',
    '{n}, is that the day turning?',
    '{n}, does that change the year for him?',
    '{n}, has he been building to this?',
    '{n}, what does that do to the points?',
    '{n}, is this the one he has been chasing?',
  ],
  rivalry: [
    '{n} — these two again.',
    '{n}, this has been coming.',
    '{n}, they have history here.',
    '{n}, somebody is going to run out of patience.',
    '{n}, how far does this go back?',
    '{n}, is anybody going to step in?',
    '{n}, does one of them have to give?',
    '{n}, where does this end?',
  ],
  defence: [
    '{n}, could he have done anything about that?',
    '{n}, was there anywhere to go?',
    '{n}, is that on him?',
    '{n}, what would you have done?',
    '{n}, did he leave him room?',
    '{n}, was that ever going to hold?',
    '{n}, is that hard racing or is that too much?',
    '{n}, could you have covered that?',
  ],
  stretch: [
    '{n}, that has been building.',
    '{n}, how long has he had that in hand?',
    '{n}, this did not happen all at once.',
    '{n}, you saw this coming.',
    '{n}, has anything changed out there?',
    '{n}, is anybody getting better?',
    '{n}, who is quietly winning this stretch?',
    '{n}, what are you watching?',
  ],
  move: [
    '{n}?',
    '{n}, go ahead.',
    '{n}, your read.',
    '{n} — what did you make of it?',
    '{n}, how did he get that done?',
    '{n}, was that there for anybody?',
    '{n}, talk me through it.',
    '{n}, did you see that coming?',
  ],
};

export function fallbackCue(to: BoothMember, beat: BoothBeat): string {
  const pool = CUES[beat.angle] ?? CUES.move;
  /*
    Indexed on the turn, not shuffled: a seeded shuffle would make the booth's
    own record of what it just said depend on how many times it had been asked.

    ⚠ MIXED WITH THE SCORE — D-EZ-74. On the turn alone, two quiet stretches at
    turns 2 and 10 both landed on `2 % 4` and Marty threw to Reba with the same
    eight words twice in one race: *"Reba, this did not happen all at once."*
    Handoffs are rationed to one every three beats, so they are rare enough that
    a listener remembers the last one — which is exactly why a collision shows.
    The score is a second dimension the turn number does not carry — except
    that a QUIET beat scores zero by definition, so the two quiet stretches
    collided all over again. The line's own length is the third, and it is the
    one that actually varies between two beats that are structurally identical.
    The shelves went from four throws to eight in the same change.
  */
  return pool[Math.abs(beat.turn * 5 + beat.score + beat.line.length) % pool.length].replace('{n}', to.short);
}

/** Every fallback throw there is, so a test can audit the pool for repetition. */
export const ALL_CUES: readonly string[] = Object.values(CUES).flat();

// ===========================================================================
// D-EZ-111 — A FIXED CAST PER LEAGUE
// ===========================================================================
//
// Howard, 5 Oct 2026: "the commentary sucks". Part of why: the same three
// strangers called every league, and none of them had a past. A booth a fan
// enjoys is people he gets to know — the play-by-play voice who goes big on
// the peaks, the old driver who has opinions and remembers his own wars, the
// pit reporter who gets to the car first.
//
// THE CAST IS HASHED FROM THE LEAGUE KEY — never from engine dice — so a league
// keeps the same booth for its whole life and two leagues usually hear
// different people. Nothing here is stored; save/load re-derives it.
//
// ⚠ HOWARD'S RECAST RULING STANDS (17 Aug 2026): "one of the commentators
// becomes the girl and the sideline becomes the boy." Every hashed booth pairs
// a woman and a man upstairs, and pit road is always a man.
//
// ⚠ EVERYTHING ABOUT AN ANALYST'S PAST IS FICTION ABOUT THE ANALYST, fixed in
// this table. It is never a fact about the league: the old rival raced before
// the league began and is never a league driver, and the stats are printed
// here so every line that cites them can be checked against this table (the
// grounding test does exactly that).

/** What an analyst leans towards when picking a driver before the green. */
export type AnalystLean = 'veterans' | 'form' | 'underdogs' | 'qualifying';
/** The kind of racing an analyst has no patience for. */
export type AnalystGrudge = 'wreckers' | 'hotheads' | 'pit-road' | 'restarts';
export type HostStyle = 'big' | 'straight' | 'dry' | 'folksy';

export interface AnalystPast {
  /** Seasons he or she drove. */
  seasons: number;
  wins: number;
  titles: number;
  /** Track category printed on the cards, where most of the wins came. */
  strongKind: 'Short Track' | 'Superspeedway' | 'Intermediate Track' | 'Road Course';
  strongWins: number;
  /** An old rival from before the league. Never a league driver. */
  rival: string;
  /** Seasons the two of them went at it. */
  rivalYears: number;
  lean: AnalystLean;
  grudge: AnalystGrudge;
}

interface PoolEntry {
  poolId: string;
  name: string;
  pronoun: 'he' | 'she';
  bio: string;
}
interface HostEntry extends PoolEntry { style: HostStyle }
interface AnalystEntry extends PoolEntry { past: AnalystPast }

/**
 * THE POOLS. Every name invented, and none of them in the field generator's
 * vocabulary (`field.ts`) — except the original three, kept so a league that
 * hashes onto them still hears the booth Howard already knows.
 */
export const HOST_POOL: readonly HostEntry[] = [
  { poolId: 'marty-kell', name: 'Marty Kell', pronoun: 'he', style: 'straight', bio: 'Twenty-two seasons in the chair. Calls it straight and gets out of the way.' },
  { poolId: 'hollis-greer', name: 'Hollis Greer', pronoun: 'he', style: 'big', bio: 'The big voice. Lets it rip when the race earns it and not a second before.' },
  { poolId: 'june-carraway', name: 'June Carraway', pronoun: 'she', style: 'dry', bio: 'Came up calling Saturday-night dirt on the radio. Dry, quick, never wastes a word.' },
  { poolId: 'lyle-pettibone', name: 'Lyle Pettibone', pronoun: 'he', style: 'folksy', bio: 'Talks to the grandstand like it is his front porch, and the grandstand talks back.' },
  { poolId: 'darla-mabry', name: 'Darla Mabry', pronoun: 'she', style: 'big', bio: 'Thirty years of finishes called at full volume. Nobody calls a checkered flag like Darla.' },
  { poolId: 'ross-whitlock', name: 'Ross Whitlock', pronoun: 'he', style: 'dry', bio: 'Former newspaperman. Says it once, says it right, moves on.' },
  { poolId: 'nora-kincaid', name: 'Nora Kincaid', pronoun: 'she', style: 'straight', bio: 'The steadiest voice in the business. Knows where every car is and says so.' },
  { poolId: 'beau-castellaw', name: 'Beau Castellaw', pronoun: 'he', style: 'folksy', bio: 'Grew up in a grandstand and never really left it. Loves a short-track Saturday.' },
];

export const ANALYST_POOL: readonly AnalystEntry[] = [
  { poolId: 'reba-vance', name: 'Reba Vance', pronoun: 'she', bio: 'Eleven years on the tour, two wins at the short tracks. Knows what the car is telling the driver.',
    past: { seasons: 11, wins: 2, titles: 0, strongKind: 'Short Track', strongWins: 2, rival: 'Lottie Pruitt', rivalYears: 6, lean: 'underdogs', grudge: 'wreckers' } },
  { poolId: 'dub-ashworth', name: 'Dub Ashworth', pronoun: 'he', bio: 'Seventeen seasons, twenty-three wins and a title. Still thinks he could get in one and win.',
    past: { seasons: 17, wins: 23, titles: 1, strongKind: 'Superspeedway', strongWins: 9, rival: 'Royce Hambrick', rivalYears: 8, lean: 'veterans', grudge: 'hotheads' } },
  { poolId: 'glenda-truitt', name: 'Glenda Truitt', pronoun: 'she', bio: 'Nine seasons, five wins on the mile-and-a-halves. Reads a race three moves ahead.',
    past: { seasons: 9, wins: 5, titles: 0, strongKind: 'Intermediate Track', strongWins: 4, rival: 'Fess Ogburn', rivalYears: 4, lean: 'form', grudge: 'pit-road' } },
  { poolId: 'hap-fontenot', name: 'Hap Fontenot', pronoun: 'he', bio: 'Fourteen seasons out of the bayou, eleven wins. Never met a restart he did not like.',
    past: { seasons: 14, wins: 11, titles: 0, strongKind: 'Short Track', strongWins: 7, rival: 'Cal Dorsett', rivalYears: 5, lean: 'qualifying', grudge: 'hotheads' } },
  { poolId: 'lamar-sizemore', name: 'Lamar Sizemore', pronoun: 'he', bio: 'Two titles, thirty-one wins, twenty seasons. Has an opinion and you are about to hear it.',
    past: { seasons: 20, wins: 31, titles: 2, strongKind: 'Intermediate Track', strongWins: 14, rival: 'Earnest Whigham', rivalYears: 9, lean: 'form', grudge: 'restarts' } },
  { poolId: 'coy-strickland', name: 'Coy Strickland', pronoun: 'he', bio: 'Twelve seasons, six wins, every one of them on the big tracks. Still holds a grudge or two.',
    past: { seasons: 12, wins: 6, titles: 0, strongKind: 'Superspeedway', strongWins: 6, rival: 'Tib Larrabee', rivalYears: 7, lean: 'underdogs', grudge: 'wreckers' } },
  { poolId: 'mavis-oakes', name: 'Mavis Oakes', pronoun: 'she', bio: 'Thirteen seasons, and three wins on the road courses when nobody gave her a chance there.',
    past: { seasons: 13, wins: 3, titles: 0, strongKind: 'Road Course', strongWins: 3, rival: 'Purvis Langham', rivalYears: 5, lean: 'qualifying', grudge: 'restarts' } },
  { poolId: 'virgil-teague', name: 'Virgil Teague', pronoun: 'he', bio: 'Sixteen seasons, fourteen wins, a title he will tell you about. Old school to the bone.',
    past: { seasons: 16, wins: 14, titles: 1, strongKind: 'Short Track', strongWins: 10, rival: 'Odell Swofford', rivalYears: 10, lean: 'veterans', grudge: 'pit-road' } },
];

export const PIT_POOL: readonly PoolEntry[] = [
  { poolId: 'ned-coyle', name: 'Ned Coyle', pronoun: 'he', bio: 'Pit road and the garage. First to the car, every time.' },
  { poolId: 'gil-haskett', name: 'Gil Haskett', pronoun: 'he', bio: 'Knows every crew chief by his first name and every hauler by its paint.' },
  { poolId: 'abner-pruett', name: 'Abner Pruett', pronoun: 'he', bio: 'Former tire changer. Watches the stop, not the scoreboard.' },
  { poolId: 'rudy-tackett', name: 'Rudy Tackett', pronoun: 'he', bio: 'Pit road since the bias-ply days. Gets the word nobody else can get.' },
  { poolId: 'lew-pinckney', name: 'Lew Pinckney', pronoun: 'he', bio: 'Fastest legs in the garage. If a car stops, he is already there.' },
  { poolId: 'mose-fairley', name: 'Mose Fairley', pronoun: 'he', bio: 'Quiet and patient, and the drivers trust him — which is why they talk to him.' },
];

/** The whole booth for one league. */
export interface Cast {
  host: BoothMember;
  analyst: BoothMember;
  pit: BoothMember;
  hostStyle: HostStyle;
  past: AnalystPast;
}

const SEAT_WORDS: Record<MemberId, string> = { host: 'Play-by-play', analyst: 'Analyst', pit: 'Pit road' };

function member(seat: MemberId, e: PoolEntry): BoothMember {
  const [first, ...rest] = e.name.split(' ');
  return {
    id: seat,
    name: e.name,
    short: first,
    seat: SEAT_WORDS[seat],
    bio: e.bio,
    initials: `${first[0]}${(rest.at(-1) ?? first)[0]}`,
    poolId: e.poolId,
    pronoun: e.pronoun,
  };
}

/**
 * A booth from three pool ids. Unknown ids fall back to the default chair —
 * the Worker builds its prompt from this, so a browser cannot smuggle a
 * description in: it can only name somebody who is already in the table.
 */
export function castByIds(ids: { host?: unknown; analyst?: unknown; pit?: unknown }): Cast {
  const h = HOST_POOL.find((x) => x.poolId === ids.host) ?? HOST_POOL[0];
  const a = ANALYST_POOL.find((x) => x.poolId === ids.analyst) ?? ANALYST_POOL[0];
  const p = PIT_POOL.find((x) => x.poolId === ids.pit) ?? PIT_POOL[0];
  return { host: member('host', h), analyst: member('analyst', a), pit: member('pit', p), hostStyle: h.style, past: a.past };
}

/** The default booth — an exhibition, or a league before it has a key. */
export const DEFAULT_CAST: Cast = castByIds({ host: 'marty-kell', analyst: 'reba-vance', pit: 'ned-coyle' });

export function castIds(cast: Cast): { host: string; analyst: string; pit: string } {
  return { host: cast.host.poolId ?? 'marty-kell', analyst: cast.analyst.poolId ?? 'reba-vance', pit: cast.pit.poolId ?? 'ned-coyle' };
}

/**
 * THE LEAGUE'S BOOTH. A pure function of the league key (FNV, not a die).
 *
 * `avoid` is the league's first roster's names: a booth member who shares a
 * first name or surname with a driver makes a name ambiguous on the air (the
 * default booth has exactly that problem with the generator's "Marty"), so a
 * colliding candidate is skipped — still deterministic, because the first
 * roster of a league never changes.
 */
export function castFor(leagueKey: string, avoid: readonly string[] = []): Cast {
  const taken = new Set(avoid.flatMap((n) => n.split(/\s+/)));
  const clash = (e: PoolEntry) => e.name.split(' ').some((w) => taken.has(w));
  const choose = <T extends PoolEntry>(pool: readonly T[], salt: string, ok: (e: T) => boolean): T => {
    const start = stableHash(`booth|${salt}|${leagueKey}`) % pool.length;
    for (let pass = 0; pass < 2; pass++) {
      for (let i = 0; i < pool.length; i++) {
        const e = pool[(start + i) % pool.length];
        if (ok(e) && (pass === 1 || !clash(e))) return e;
      }
    }
    return pool[start];
  };
  const analyst = choose(ANALYST_POOL, 'analyst', () => true);
  // Howard's ruling: a woman and a man upstairs.
  const host = choose(HOST_POOL, 'host', (e) => e.pronoun !== analyst.pronoun);
  const pit = choose(PIT_POOL, 'pit', () => true);
  return castByIds({ host: host.poolId, analyst: analyst.poolId, pit: pit.poolId });
}

/** Every word this booth may say about itself: names and the analyst's old rival. */
export function castWords(cast: Cast): string[] {
  return [cast.host, cast.analyst, cast.pit]
    .flatMap((m) => [...m.name.split(/\s+/), m.short])
    .concat(cast.past.rival.split(/\s+/));
}

/** Every word any booth can say about itself, for the exempt-list audit. */
export const ALL_CAST_WORDS: readonly string[] = [
  ...new Set(
    [...HOST_POOL, ...ANALYST_POOL, ...PIT_POOL]
      .flatMap((e) => e.name.split(' '))
      .concat(ANALYST_POOL.flatMap((e) => e.past.rival.split(' '))),
  ),
];
